import React, {
    useEffect,
    useState,
} from "react";

import "./../auth/Login.css";

import {
    FiBarChart2,
    FiDatabase,
    FiEye,
    FiEyeOff,
    FiLock,
    FiMonitor,
    FiServer,
    FiShield,
    FiSmartphone,
    FiUsers,
} from "react-icons/fi";

import {
    useHistory,
} from "react-router-dom";

import usePlatformAuth
    from "../../hooks/usePlatformAuth";

import useSwal
    from "../../hooks/useSwal";


const PlatformLogin = () => {

    const history =
        useHistory();


    const swal =
        useSwal();


    const {
        platformLogin,
        platformLoading,
        isPlatformAuthenticated,
    } =
        usePlatformAuth();


    /*
    |--------------------------------------------------------------------------
    | FORM
    |--------------------------------------------------------------------------
    */

    const [
        form,
        setForm,
    ] =
        useState({
            mobile:
                "",

            password:
                "",
        });


    const [
        errors,
        setErrors,
    ] =
        useState(
            {}
        );


    const [
        showPassword,
        setShowPassword,
    ] =
        useState(
            false
        );


    /*
    |--------------------------------------------------------------------------
    | ALREADY LOGGED IN
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            if (
                isPlatformAuthenticated
            ) {

                history.replace(
                    "/platform/dashboard"
                );

            }

        },
        [
            isPlatformAuthenticated,
            history,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | CHANGE
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        event
    ) => {

        const {
            name,
            value,
        } =
            event.target;


        let nextValue =
            value;


        if (
            name ===
            "mobile"
        ) {

            nextValue =
                value
                    .replace(
                        /\D/g,
                        ""
                    )
                    .slice(
                        0,
                        10
                    );

        }


        setForm(
            (
                previous
            ) => ({
                ...previous,

                [name]:
                    nextValue,
            })
        );


        setErrors(
            (
                previous
            ) => ({
                ...previous,

                [name]:
                    "",

                form:
                    "",
            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | VALIDATE
    |--------------------------------------------------------------------------
    */

    const validate =
        () => {

            const nextErrors =
                {};


            if (
                !/^[6-9]\d{9}$/.test(
                    form.mobile
                )
            ) {

                nextErrors.mobile =
                    "Enter a valid 10-digit mobile number.";

            }


            if (
                !String(
                    form.password ||
                    ""
                ).trim()
            ) {

                nextErrors.password =
                    "Password is required.";

            }


            setErrors(
                nextErrors
            );


            return (
                Object.keys(
                    nextErrors
                ).length ===
                0
            );

        };


    /*
    |--------------------------------------------------------------------------
    | LOGIN
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (
            event
        ) => {

            event.preventDefault();


            if (
                !validate()
            ) {
                return;
            }


            const response =
                await platformLogin({

                    mobile:
                        form.mobile,

                    password:
                        form.password,

                });


            if (
                response?.success
            ) {

                await swal.success({

                    title:
                        "Platform Login Successful",

                    text:
                        "Welcome to ComKwik Platform Administration.",

                });


                history.replace(
                    "/platform/dashboard"
                );


                return;

            }


            const message =
                response?.message ||
                "Unable to login to platform administration.";


            setErrors({
                form:
                    message,
            });


            swal.error({

                title:
                    "Login Failed",

                text:
                    message,

            });

        };


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div className="comkwik-login-page">

            <div className="comkwik-login-background-grid" />

            <div className="comkwik-login-glow comkwik-login-glow-left" />

            <div className="comkwik-login-glow comkwik-login-glow-right" />


            <main className="comkwik-login-shell">

                {/*
                |--------------------------------------------------------------------------
                | PLATFORM INFORMATION
                |--------------------------------------------------------------------------
                */}

                <section className="comkwik-business-panel">

                    <header className="comkwik-business-header">

                        <div className="comkwik-brand">

                            <div className="comkwik-brand-logo">

                                <FiMonitor />

                            </div>


                            <div className="comkwik-brand-text">

                                <span>

                                    COMKWIK

                                </span>


                                <strong>

                                    Platform Administration

                                </strong>

                            </div>

                        </div>


                        <div className="comkwik-secure-badge">

                            <FiShield />

                            Platform Super Admin

                        </div>

                    </header>

                    <section className="comkwik-business-hero">
                        <div className="comkwik-business-hero-copy">
                            <span className="comkwik-hero-label">
                                COMKWIK PLATFORM CONTROL
                            </span>
                            <h1>
                                Control every business
                                from one central
                                platform.
                            </h1>
                            <p>
                                Manage businesses,
                                subscriptions,
                                storefront access,
                                platform activity and
                                SaaS operations from
                                the central ComKwik
                                administration portal.

                            </p>

                        </div>

                    </section>


                    <section className="comkwik-feature-grid">

                        <article className="comkwik-feature-card">

                            <div className="comkwik-feature-icon">

                                <FiDatabase />

                            </div>


                            <div className="comkwik-feature-content">

                                <strong>

                                    Business Management

                                </strong>


                                <p>

                                    Manage every tenant
                                    registered on the
                                    platform.

                                </p>

                            </div>

                        </article>


                        <article className="comkwik-feature-card">

                            <div className="comkwik-feature-icon">

                                <FiUsers />

                            </div>


                            <div className="comkwik-feature-content">

                                <strong>

                                    Tenant Administration

                                </strong>


                                <p>

                                    Monitor active,
                                    suspended and new
                                    businesses.

                                </p>

                            </div>

                        </article>


                        <article className="comkwik-feature-card">

                            <div className="comkwik-feature-icon">

                                <FiBarChart2 />

                            </div>


                            <div className="comkwik-feature-content">

                                <strong>

                                    Platform Analytics

                                </strong>


                                <p>

                                    View business growth
                                    and subscription
                                    performance.

                                </p>

                            </div>

                        </article>


                        <article className="comkwik-feature-card">

                            <div className="comkwik-feature-icon">

                                <FiServer />

                            </div>


                            <div className="comkwik-feature-content">

                                <strong>

                                    Platform Control

                                </strong>


                                <p>

                                    Central operational
                                    control for the
                                    entire SaaS platform.

                                </p>

                            </div>

                        </article>

                    </section>

                </section>


                {/*
                |--------------------------------------------------------------------------
                | PLATFORM LOGIN
                |--------------------------------------------------------------------------
                */}

                <section className="comkwik-auth-panel">

                    <header className="comkwik-auth-intro">

                        <div className="comkwik-auth-intro-icon">

                            <FiShield />

                        </div>


                        <div>

                            <span>

                                PLATFORM SECURITY

                            </span>


                            <h2>

                                Super Admin Access

                            </h2>


                            <p>

                                This portal is reserved
                                for authorized ComKwik
                                platform administrators.

                            </p>

                        </div>

                    </header>


                    <div className="comkwik-login-card">

                        <div className="comkwik-card-heading">

                            <div className="comkwik-card-heading-icon">

                                <FiShield />

                            </div>


                            <span>

                                PLATFORM LOGIN

                            </span>


                            <h2>

                                Welcome Back

                            </h2>


                            <p>

                                Enter your Platform
                                Super Admin mobile
                                number and password.

                            </p>

                        </div>


                        {errors.form ? (

                            <div className="comkwik-login-error">

                                <FiShield />

                                <span>

                                    {
                                        errors.form
                                    }

                                </span>

                            </div>

                        ) : null}


                        <form
                            className="comkwik-login-form"
                            onSubmit={
                                handleSubmit
                            }
                            noValidate
                        >

                            {/*
                            |--------------------------------------------------------------------------
                            | MOBILE
                            |--------------------------------------------------------------------------
                            */}

                            <div className="comkwik-form-field">

                                <label htmlFor="platform-mobile">

                                    Mobile Number

                                </label>


                                <div
                                    className={`comkwik-input-wrapper ${
                                        errors.mobile
                                            ? "has-error"
                                            : ""
                                    }`}
                                >

                                    <span className="comkwik-input-icon">

                                        <FiSmartphone />

                                    </span>


                                    <span className="comkwik-country-code">

                                        +91

                                    </span>


                                    <input
                                        id="platform-mobile"
                                        type="tel"
                                        name="mobile"
                                        value={
                                            form.mobile
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        placeholder="Enter mobile number"
                                        inputMode="numeric"
                                        autoComplete="username"
                                        maxLength={
                                            10
                                        }
                                        disabled={
                                            platformLoading
                                        }
                                    />

                                </div>


                                {errors.mobile ? (

                                    <small className="comkwik-field-error">

                                        {
                                            errors.mobile
                                        }

                                    </small>

                                ) : (

                                    <small className="comkwik-field-help">

                                        Platform Super
                                        Admin mobile
                                        number.

                                    </small>

                                )}

                            </div>


                            {/*
                            |--------------------------------------------------------------------------
                            | PASSWORD
                            |--------------------------------------------------------------------------
                            */}

                            <div className="comkwik-form-field">

                                <label htmlFor="platform-password">

                                    Password

                                </label>


                                <div
                                    className={`comkwik-input-wrapper ${
                                        errors.password
                                            ? "has-error"
                                            : ""
                                    }`}
                                >

                                    <span className="comkwik-input-icon">

                                        <FiLock />

                                    </span>


                                    <input
                                        id="platform-password"
                                        type={
                                            showPassword
                                                ? "text"
                                                : "password"
                                        }
                                        name="password"
                                        value={
                                            form.password
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        placeholder="Enter password"
                                        autoComplete="current-password"
                                        disabled={
                                            platformLoading
                                        }
                                    />


                                    <button
                                        type="button"
                                        className="comkwik-password-toggle"
                                        onClick={() =>
                                            setShowPassword(
                                                (
                                                    previous
                                                ) =>
                                                    !previous
                                            )
                                        }
                                    >

                                        {showPassword ? (

                                            <FiEyeOff />

                                        ) : (

                                            <FiEye />

                                        )}

                                    </button>

                                </div>


                                {errors.password ? (

                                    <small className="comkwik-field-error">

                                        {
                                            errors.password
                                        }

                                    </small>

                                ) : null}

                            </div>


                            <div className="comkwik-login-protection">

                                <FiShield />

                                <span>

                                    Platform access uses
                                    an isolated
                                    authentication
                                    session.

                                </span>

                            </div>


                            <button
                                type="submit"
                                className="comkwik-login-button"
                                disabled={
                                    platformLoading
                                }
                            >

                                {platformLoading ? (

                                    <>

                                        <span className="comkwik-login-spinner" />

                                        Signing In...

                                    </>

                                ) : (

                                    <>

                                        Login to Platform

                                        <FiBarChart2 />

                                    </>

                                )}

                            </button>

                        </form>


                        <div className="comkwik-card-footer">

                            <FiShield />

                            <span>

                                Platform Super Admin
                                access only

                            </span>

                        </div>

                    </div>

                </section>

            </main>

        </div>

    );

};


export default PlatformLogin;